import { BreakdownDonut, OTHER_COLOR, type BreakdownBucket } from "./BreakdownDonut";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { labelOr } from "@/shared/lib/label";
import {
  instrumentTypeLabelKey,
  isInstrumentType,
  type InstrumentType,
} from "@/shared/lib/instrument-type";
import { formatPercent, parsePercentToAmountScale } from "@/shared/lib/amount";
import { consolidationStateOf, isNegativeNavOf } from "@/features/portfolio/api/position";
import type { Position, PositionSummary } from "@/features/portfolio/api/position";

interface AllocationDonutProps {
  positions: Position[];
  summary?: PositionSummary | null;
  summaryUnavailable?: boolean;
}

const COLOR_BY_INSTRUMENT_TYPE: Record<InstrumentType, string> = {
  debt: "hsl(var(--chart-1))",
  equity: "hsl(var(--chart-2))",
  currency: "hsl(var(--chart-3))",
  crypto: "hsl(var(--chart-4))",
  fund: "hsl(var(--chart-5))",
  option: "hsl(var(--chart-6))",
};

export const AllocationDonut = ({ positions, summary, summaryUnavailable }: AllocationDonutProps) => {
  const { t } = useLanguage();
  const { numberFormat } = useNumberFormat();

  // A summary that never answered is not a summary that answered with nothing: the
  // "prices are on their way" copy below is a claim about market-data coverage, and the
  // page states the failure — with a retry — in its own card instead.
  if (summaryUnavailable) return null;

  // A weight the client cannot read is an omitted slice, never a zero-size one. The
  // backend emits `null` for a share of a zero whole, and `percent_string` carries no
  // magnitude bound, so `parseFloat(...) || 0` drew an invisible wedge with an empty
  // label for a fact the reader was entitled to be told nothing about. A donut of the
  // slices that could be read is honest; a donut with a phantom slice in it is not.
  const readableAllocations = (summary?.allocations || []).flatMap((allocation) => {
    const share = parsePercentToAmountScale(allocation.weight);
    if (share == null) return [];
    return [{ instrumentType: allocation.instrument_type, weightLabel: formatPercent(allocation.weight, numberFormat), share }];
  });
  const buckets: BreakdownBucket[] = readableAllocations.map((allocation) => ({
    key: allocation.instrumentType,
    label: labelOr(t, instrumentTypeLabelKey(allocation.instrumentType), allocation.instrumentType),
    weightLabel: allocation.weightLabel,
    share: allocation.share,
    color: isInstrumentType(allocation.instrumentType)
      ? COLOR_BY_INSTRUMENT_TYPE[allocation.instrumentType]
      : OTHER_COLOR,
  }));

  const notice = buckets.length === 0 ? noticeKey(summary, positions) : null;

  return (
    <BreakdownDonut
      title={t("portfolio.allocationTitle")}
      testIdPrefix="allocation"
      buckets={buckets}
      notice={notice === null ? null : t(notice)}
      wholeIsNegative={isNegativeNavOf(summary)}
    />
  );
};

/** Why no asset class has a readable share, or `null` when there is nothing to say. */
function noticeKey(summary: PositionSummary | null | undefined, positions: Position[]): string | null {
  // Buckets with no readable share at all. The backend emits a bucket only once the
  // consolidated NAV resolves, so this is the fully-netting book: every share is a
  // share of zero, which is undefined rather than pending. Saying prices are on their
  // way here would name an outage that is not happening.
  if ((summary?.allocations || []).length > 0) return "portfolio.allocationZeroNav";
  if (positions.length === 0) return null;
  // Only a rate outage earns the exchange-rate copy: the same null also means nothing was
  // priced, and blaming FX for a pricing gap misdescribes the outage to the reader.
  return consolidationStateOf(summary) === "rate-missing"
    ? "portfolio.allocationFxUnavailable"
    : "portfolio.allocationPending";
}
